import React from 'react';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import { useSearchCommoditiesQuery } from '../../redux/api/commodities/commoditiesApi';
import {
  CommodityTableAttributes,
  SearchCommoditiesResponseDto
} from '../../redux/api/commodities/commodities.model';
import { CommodityFormModal } from '../../pages/Commodities/CommodityFormModal';
import { TextField, InputAdornment } from '@mui/material';
import { useCommoditySettings } from '../../hooks/useCommoditySettings';

interface TwCommoditySelectorProps {
  id: string;
  value?: string | null;
  divisionId?: string | null;
  textValue?: string | null;
  commodityId?: string | null;
  onSelect: (option: CommodityTableAttributes | undefined, targetId: string) => void;
  onChange: (option: string) => void;
}

export const TwCommoditySelector: React.FC<TwCommoditySelectorProps> = ({
  id,
  value,
  textValue,
  commodityId,
  onSelect,
  onChange
}) => {
  const [commodityOptions, setCommodityOptions] = React.useState<TwAutocompleteOption[]>([]);
  const [selectedOption, setSelectedOption] = React.useState<TwAutocompleteOption | null>(null);
  const [openCreateCommodity, setOpenCreateCommodity] = React.useState(false);

  const { isCommodityIdEnabled } = useCommoditySettings();

  const {
    data: Commodities,
    isLoading,
    isError,
    isFetching
  } = useSearchCommoditiesQuery({
    filters: {}
  }, 
  {
    selectFromResult: ({ data, ...rest }) => ({
      data: data?.content,
      ...rest
    })
  });

  React.useEffect(() => {
    if (Commodities) {
      const options = transformCommoditys(Commodities);
      setCommodityOptions(options);
      if (value) {
        const option = options.find((opt) => opt.value === value);
        if (option) {
          setSelectedOption({
            ...option,
            label: textValue ?? option.label
          });
        } else if (textValue) {
          setSelectedOption({
            id: 'free-text',
            value: textValue,
            label: textValue
          });
        } else {
          setSelectedOption(null);
        }
      } else if (textValue) {
        setSelectedOption({
          id: 'free-text',
          value: textValue,
          label: textValue
        });
      }
    }
  }, [Commodities, value, textValue]);

  const transformCommoditys = (
    tables: SearchCommoditiesResponseDto[]
  ): TwAutocompleteOption[] => {
    return tables.map((table) => ({
      id: table.id,
      value: table.id,
      label: (table.commodity_id && isCommodityIdEnabled) ? `${table.name} (ID ${table.commodity_id})` : table.name
    }));
  };

  const handleChange = (_: unknown, newValue: TwAutocompleteOption | null): void => {
    setSelectedOption(newValue);

    if (typeof newValue === 'string') {
      const findCommodityByName = Commodities?.find((table) => table.name === newValue);
      if (findCommodityByName) {
        onSelect(findCommodityByName as CommodityTableAttributes, id);
      } else {
        onChange(newValue);
      }

      return;
    }

    if (!newValue) {
      onSelect(undefined, id);
      return;
    }
    
    const Commodity = Commodities?.find((table) => table.id === newValue.value);
    onSelect(Commodity as CommodityTableAttributes, id);
  };

  const handleOpenCreateCommodity = (): void => {
    setOpenCreateCommodity(true);
  };

  if (isLoading || isFetching) {
    return (
      <TwAutocomplete
        name="Commodity"
        optionList={[]}
        value={selectedOption}
        placeholder="Loading commodities..."
        disabled={true}
      />
    );
  }

  if (isError) {
    return <div>Error loading commodities</div>;
  }

  return (
    <>
      <TwAutocomplete
        name="Commodity"
        placeholder="Select a commodity"
        optionList={commodityOptions}
        renderOption={(props, option): React.ReactNode => {
          return <RenderOption key={option.id} props={props} option={option} />;
        }}
        value={selectedOption}
        onChange={handleChange}
        freeText={true}
        listAction={{
          label: 'New commodity',
          action: (): void => handleOpenCreateCommodity()
        }}
        renderInput={
          isCommodityIdEnabled
            ? (params): JSX.Element => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {commodityId && (
                          <InputAdornment position="end" sx={{ mr: 5, fontWeight: 500 }}>
                            (ID {commodityId})
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )
            : undefined
        }
      />

      <CommodityFormModal
        isDuplicateMode={false}
        commodityId={undefined}
        open={openCreateCommodity}
        onClose={(): void => setOpenCreateCommodity(false)}
        onSuccess={(newCommodity: CommodityTableAttributes): void => {
          onSelect(newCommodity, id);
          setSelectedOption({
            id: newCommodity.id,
            value: newCommodity.id,
            label: newCommodity.name
          });
          setOpenCreateCommodity(false);
        }}
      />
    </>
  );
};
